import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError } from '../api/client';
import { mailingApi, type MailingOverview, type MailingSpace } from '../api/mailing';
import { useFeather } from '../shell/useFeather';
import { ArcPage, ArcPageHeader, ArcSummaryGrid, ArcSummaryMetric } from '../components/arc/primitives/arc-primitives';
import { Button } from '../components/arc/button/button';
import { Card } from '../components/arc/card/card';
import { DonutChart } from '../components/arc/donut-chart/donut-chart';
import { MetricCard } from '../components/arc/metric-card/metric-card';
import { Progress } from '../components/arc/progress/progress';

const count = (n: number) => n.toLocaleString('cs-CZ');
const pct = (n: number | null) => (n == null ? '-' : `${n.toLocaleString('cs-CZ', { maximumFractionDigits: 1 })} %`);
const COUNTRY_NAME: Record<string, string> = { CZ: 'Česko', SK: 'Slovensko' };

export function MailingDashboardPage({ space, eyebrow }: { space: MailingSpace; eyebrow: string }) {
  const api = useMemo(() => mailingApi(space), [space]);
  const [data, setData] = useState<MailingOverview | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    setData(null);
    api.overview()
      .then(setData)
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Načtení selhalo'));
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  useFeather([data, error]);

  const heading = <ArcPageHeader eyebrow={eyebrow} title="Dashboard" description="Stav adresáře a výkon odeslaných kampaní" />;

  if (error) return (
    <ArcPage className="p1-mailing mailing-overview">
      {heading}
      <div className="mailing-overview-message is-error" role="alert">
        <span className="mailing-overview-message-icon"><i data-feather="alert-circle" /></span>
        <div><strong>Dashboard mailingu se nepodařilo načíst</strong><p>Data jsou dočasně nedostupná. Zkuste načtení zopakovat.</p><small>{error}</small></div>
        <Button type="button" variant="secondary" size="sm" onClick={load}>Načíst znovu</Button>
      </div>
    </ArcPage>
  );
  if (!data) return <ArcPage className="p1-mailing mailing-overview">{heading}<div className="prototype-page-loading" aria-label="Načítám"><span /><span /><span /><span /></div></ArcPage>;

  const p = data.performance;
  const subscribed = data.totals.contacts - data.totals.unsubscribed;
  const subscribedRate = data.totals.contacts ? Math.round((subscribed * 1000) / data.totals.contacts) / 10 : 0;
  const maxCountryContacts = Math.max(1, ...data.countries.map((c) => c.contacts));
  return (
    <ArcPage className="p1-mailing mailing-overview">
      {heading}

      <ArcSummaryGrid>
        <ArcSummaryMetric label="Kontakty celkem" value={data.totals.contacts} context="ve všech trzích" />
        <ArcSummaryMetric label="Přihlášení k odběru" value={subscribed} context={`${pct(subscribedRate)} adresáře`} />
        <ArcSummaryMetric label="Odeslané kampaně" value={p.campaigns} context="ve sledovaném období" />
        <ArcSummaryMetric label="Doručené e-maily" value={p.delivered} context="z odeslaných kampaní" />
      </ArcSummaryGrid>

      <div className="mailing-overview-grid">
        <Card title="Kontakty podle trhu" className="mailing-overview-panel mailing-audience-panel">
          <p className="mailing-panel-meta">{data.countries.length} {data.countries.length === 1 ? 'trh' : data.countries.length < 5 ? 'trhy' : 'trhů'}</p>
          <div className="mailing-audience-main">
            <DonutChart
              label="Přihlášení a odhlášení kontaktů"
              data={[{ key: 'subscribed', label: 'Přihlášení', value: subscribed, color: 'var(--p1-green)' }, { key: 'unsubscribed', label: 'Odhlášení', value: data.totals.unsubscribed, color: '#d3cfd9' }]}
              size={160}
              thickness={20}
              groupBelow={0}
              maxSegments={2}
              legend={false}
              totalLabel="Přihlášeno"
              formatValue={(value) => value === data.totals.contacts ? `${subscribedRate.toLocaleString('cs-CZ', { maximumFractionDigits: 1 })}%` : count(value)}
            />
            <div className="mailing-audience-totals">
              <div><i className="is-subscribed" /><span>Přihlášení</span><strong>{count(subscribed)}</strong></div>
              <div><i className="is-unsubscribed" /><span>Odhlášení</span><strong>{count(data.totals.unsubscribed)}</strong></div>
            </div>
          </div>
          <div className="mailing-market-list">
            {data.countries.map((c) => (
              <article key={c.code}>
                <span className="mailing-market-code">{c.code}</span>
                <div>
                  <header><strong id={`mailing-market-${c.code}`}>{COUNTRY_NAME[c.code] ?? c.code}</strong><span>{count(c.contacts)} kontaktů</span></header>
                  <Progress value={c.contacts} max={maxCountryContacts} aria-labelledby={`mailing-market-${c.code}`} className="mailing-market-progress" />
                  <small>{count(c.subscribed)} přihlášených · {count(c.unsubscribed)} odhlášených</small>
                </div>
              </article>
            ))}
          </div>
        </Card>

        <Card title="Zapojení příjemců" className="mailing-overview-panel mailing-performance-panel">
          <p className="mailing-panel-meta">{p.campaigns ? `${count(p.campaigns)} kampaní` : 'Čeká na první rozesílku'}</p>
          <div className="mailing-performance-lead">
            <MetricCard label="Doručené e-maily" value={p.delivered} context={p.campaigns ? `Z ${count(p.campaigns)} odeslaných kampaní` : 'Čeká na první kampaň'} />
          </div>
          <div className="mailing-rate-list">
            <article>
              <div><span id="mailing-open-rate">Open rate</span><strong>{pct(p.openRate)}</strong></div>
              <Progress value={p.openRate ?? 0} aria-labelledby="mailing-open-rate" />
              <small>{count(p.opens)} otevření z {count(p.delivered)} doručených</small>
            </article>
            <article>
              <div><span id="mailing-click-rate">Click rate</span><strong>{pct(p.clickRate)}</strong></div>
              <Progress value={p.clickRate ?? 0} aria-labelledby="mailing-click-rate" />
              <small>{count(p.clickers)} prokliků z {count(p.delivered)} doručených</small>
            </article>
          </div>
        </Card>
      </div>
    </ArcPage>
  );
}
